import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  MapPin, 
  Users, 
  Star, 
  Calendar, 
  Check, 
  Wifi, 
  Car, 
  Tv, 
  Coffee,
  ArrowLeft,
  Heart,
  Share,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';

const ListingDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [bookingData, setBookingData] = useState({
    checkIn: '',
    checkOut: '',
    guests: {
      adults: 2,
      children: 0,
      infants: 0
    }
  });
  const [bookingLoading, setBookingLoading] = useState(false);

  useEffect(() => {
    fetchListing();
  }, [id]);

  const fetchListing = async () => {
    try {
      const response = await axios.get(`/listings/${id}`);
      setListing(response.data.listing);
    } catch (error) {
      console.error('Failed to fetch listing:', error);
      toast.error('Failed to load property details');
      navigate('/listings');
    } finally {
      setLoading(false);
    }
  };

  const handleBooking = async (e) => {
    e.preventDefault();
    
    if (!user) {
      toast.error('Please login to make a booking');
      navigate('/login', { state: { from: location } });
      return;
    }

    if (!bookingData.checkIn || !bookingData.checkOut) {
      toast.error('Please select check-in and check-out dates');
      return;
    }

    // Validate dates on frontend as well
    const checkInDate = new Date(bookingData.checkIn);
    const checkOutDate = new Date(bookingData.checkOut);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (checkInDate < today) {
      toast.error('Check-in date cannot be in the past');
      return;
    }

    if (checkOutDate <= checkInDate) {
      toast.error('Check-out date must be after check-in date');
      return;
    }

    setBookingLoading(true);
    try {
      const nights = Math.ceil((checkOutDate - checkInDate) / (1000 * 60 * 60 * 24));
      const totalAmount = nights * listing.price;

      await axios.post('/bookings', {
        listing: listing._id,
        checkIn: bookingData.checkIn,
        checkOut: bookingData.checkOut,
        guests: bookingData.guests,
        totalAmount
      });

      toast.success('Booking created successfully!');
      navigate('/bookings');
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to create booking';
      toast.error(message);
    } finally {
      setBookingLoading(false);
    }
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => 
      prev === listing.images.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => 
      prev === 0 ? listing.images.length - 1 : prev - 1
    );
  };

  const amenityIcons = {
    'WiFi': Wifi,
    'Parking': Car,
    'TV': Tv,
    'Kitchen': Coffee,
  };

  const calculateTotal = () => {
    if (!bookingData.checkIn || !bookingData.checkOut) return 0;
    
    const nights = Math.ceil(
      (new Date(bookingData.checkOut) - new Date(bookingData.checkIn)) / (1000 * 60 * 60 * 24)
    );
    
    return nights * listing.price;
  };

  const totalGuests = bookingData.guests.adults + bookingData.guests.children + bookingData.guests.infants;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Property Not Found</h2>
          <button
            onClick={() => navigate('/listings')}
            className="bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition-colors"
          >
            Back to Listings
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft size={20} />
              <span>Back</span>
            </button>
            
            <div className="flex items-center space-x-4">
              <button className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors">
                <Share size={20} />
                <span>Share</span>
              </button>
              <button className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors">
                <Heart size={20} />
                <span>Save</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Title and Rating */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{listing.title}</h1>
          <div className="flex items-center space-x-4 text-gray-600">
            {listing.rating.count > 0 && (
              <div className="flex items-center">
                <Star size={16} className="text-yellow-400 fill-current mr-1" />
                <span className="font-medium">{listing.rating.average.toFixed(1)}</span>
                <span className="ml-1">({listing.rating.count} reviews)</span>
              </div>
            )}
            <div className="flex items-center">
              <MapPin size={16} className="mr-1" />
              <span>{listing.location.city}, {listing.location.state}, {listing.location.country}</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Image Gallery */}
            <div className="relative">
              <div className="aspect-w-16 aspect-h-9 rounded-xl overflow-hidden">
                <img
                  src={listing.images?.[currentImageIndex]?.url || '/images/placeholder-property.jpg'}
                  alt={listing.images?.[currentImageIndex]?.caption || listing.title}
                  className="w-full h-96 object-cover"
                />
              </div>
              
              {listing.images && listing.images.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white rounded-full p-2 shadow-lg hover:bg-gray-50 transition-colors"
                  >
                    <ChevronLeft size={20} />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white rounded-full p-2 shadow-lg hover:bg-gray-50 transition-colors"
                  >
                    <ChevronRight size={20} />
                  </button>
                  
                  <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
                    {listing.images.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentImageIndex(index)}
                        className={`w-2 h-2 rounded-full transition-colors ${
                          index === currentImageIndex ? 'bg-white' : 'bg-white bg-opacity-50'
                        }`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Property Details */}
            <div className="bg-white rounded-xl p-6 shadow-soft">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">
                  {listing.roomType.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())} hosted by {listing.host.firstName}
                </h2>
                <img
                  src={listing.host.avatar || '/images/default-avatar.jpg'}
                  alt={listing.host.firstName}
                  className="w-12 h-12 rounded-full"
                />
              </div>
              
              <div className="flex items-center space-x-4 text-gray-600 mb-4">
                <div className="flex items-center">
                  <Users size={16} className="mr-1" />
                  <span>{listing.maxGuests} guests</span>
                </div>
                <span>•</span>
                <span>{listing.bedrooms} bedroom{listing.bedrooms !== 1 ? 's' : ''}</span>
                <span>•</span>
                <span>{listing.bathrooms} bathroom{listing.bathrooms !== 1 ? 's' : ''}</span>
              </div>
              
              <p className="text-gray-700 leading-relaxed">{listing.description}</p>
            </div>

            {/* Amenities */}
            <div className="bg-white rounded-xl p-6 shadow-soft">
              <h3 className="text-xl font-semibold mb-4">What this place offers</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {listing.amenities.map((amenity, index) => {
                  const IconComponent = amenityIcons[amenity] || Check;
                  return (
                    <div key={index} className="flex items-center space-x-3">
                      <IconComponent size={20} className="text-gray-600" />
                      <span>{amenity}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* House Rules */}
            <div className="bg-white rounded-xl p-6 shadow-soft">
              <h3 className="text-xl font-semibold mb-4">House Rules</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span>Check-in</span>
                  <span className="text-gray-600">{listing.rules.checkIn}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Check-out</span>
                  <span className="text-gray-600">{listing.rules.checkOut}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Smoking</span>
                  <span className="text-gray-600">
                    {listing.rules.smokingAllowed ? 'Allowed' : 'Not allowed'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Pets</span>
                  <span className="text-gray-600">
                    {listing.rules.petsAllowed ? 'Allowed' : 'Not allowed'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Parties/Events</span>
                  <span className="text-gray-600">
                    {listing.rules.partiesAllowed ? 'Allowed' : 'Not allowed'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Booking Card */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl p-6 shadow-soft sticky top-8">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <span className="text-2xl font-bold">${listing.price}</span>
                  <span className="text-gray-600 ml-1">/ night</span>
                </div>
                {listing.rating.count > 0 && (
                  <div className="flex items-center">
                    <Star size={16} className="text-yellow-400 fill-current mr-1" />
                    <span className="font-medium">{listing.rating.average.toFixed(1)}</span>
                  </div>
                )}
              </div>

              <form onSubmit={handleBooking} className="space-y-4">
                {/* Dates */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Check-in
                    </label>
                    <input
                      type="date"
                      value={bookingData.checkIn}
                      onChange={(e) => setBookingData(prev => ({ ...prev, checkIn: e.target.value }))}
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Check-out
                    </label>
                    <input
                      type="date"
                      value={bookingData.checkOut}
                      onChange={(e) => setBookingData(prev => ({ ...prev, checkOut: e.target.value }))}
                      min={bookingData.checkIn ? 
                        new Date(new Date(bookingData.checkIn).getTime() + 24 * 60 * 60 * 1000).toISOString().split('T')[0] :
                        new Date(new Date().getTime() + 24 * 60 * 60 * 1000).toISOString().split('T')[0]
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      required
                    />
                  </div>
                </div>

                {/* Guests */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Guests
                  </label>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span>Adults</span>
                      <div className="flex items-center space-x-3">
                        <button
                          type="button"
                          onClick={() => setBookingData(prev => ({
                            ...prev,
                            guests: { ...prev.guests, adults: Math.max(1, prev.guests.adults - 1) }
                          }))}
                          className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50"
                        >
                          -
                        </button>
                        <span className="w-8 text-center">{bookingData.guests.adults}</span>
                        <button
                          type="button"
                          onClick={() => setBookingData(prev => ({
                            ...prev,
                            guests: { ...prev.guests, adults: prev.guests.adults + 1 }
                          }))}
                          disabled={totalGuests >= listing.maxGuests}
                          className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          +
                        </button>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span>Children</span>
                      <div className="flex items-center space-x-3">
                        <button
                          type="button"
                          onClick={() => setBookingData(prev => ({
                            ...prev,
                            guests: { ...prev.guests, children: Math.max(0, prev.guests.children - 1) }
                          }))}
                          className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50"
                        >
                          -
                        </button>
                        <span className="w-8 text-center">{bookingData.guests.children}</span>
                        <button
                          type="button"
                          onClick={() => setBookingData(prev => ({
                            ...prev,
                            guests: { ...prev.guests, children: prev.guests.children + 1 }
                          }))}
                          disabled={totalGuests >= listing.maxGuests}
                          className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          +
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  {totalGuests > listing.maxGuests && (
                    <p className="text-red-500 text-sm mt-2">
                      This property can accommodate maximum {listing.maxGuests} guests
                    </p>
                  )}
                </div>

                {/* Price Breakdown */}
                {bookingData.checkIn && bookingData.checkOut && (
                  <div className="border-t pt-4 space-y-2">
                    <div className="flex justify-between">
                      <span>${listing.price} x {Math.ceil((new Date(bookingData.checkOut) - new Date(bookingData.checkIn)) / (1000 * 60 * 60 * 24))} nights</span>
                      <span>${calculateTotal()}</span>
                    </div>
                    <div className="flex justify-between font-semibold text-lg border-t pt-2">
                      <span>Total</span>
                      <span>${calculateTotal()}</span>
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={bookingLoading || totalGuests > listing.maxGuests}
                  className="w-full bg-primary-600 text-white py-3 rounded-lg font-medium hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {bookingLoading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mx-auto"></div>
                  ) : (
                    'Reserve'
                  )}
                </button>
              </form>
              
              <p className="text-center text-gray-500 text-sm mt-4">
                You won't be charged yet
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListingDetailPage;
